// Public legal + support pages (no auth). The iOS app links here from
// onboarding and Settings (Constants.Legal), and App Store Connect lists
// /privacy and /support. Bilingual like the rest of the operator's apps:
// English first, German below. Content is static on purpose — no i18n keys,
// so a translation drift can never change what a legal text says.
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Wordmark } from "@/components/logo";

export const SUPPORT_EMAIL = "support@edupause.app";
const UPDATED_EN = "10 October 2026";
const UPDATED_DE = "10. Oktober 2026";

function Mail() {
  return (
    <a className="text-brand-600 underline" href={`mailto:${SUPPORT_EMAIL}`}>
      {SUPPORT_EMAIL}
    </a>
  );
}

function LegalLayout({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-black/5">
        <div className="container-page flex items-center justify-between py-4">
          <Link to="/" aria-label="EduPause home">
            <Wordmark className="text-lg text-ink" />
          </Link>
        </div>
      </header>
      <main className="container-page max-w-3xl py-10">
        <h1 className="text-3xl font-bold text-ink">{title}</h1>
        <div className="legal-body mt-6 space-y-4 text-[15px] leading-relaxed text-ink-soft [&_h2]:mt-10 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-ink [&_h3]:mt-6 [&_h3]:font-semibold [&_h3]:text-ink [&_li]:ml-5 [&_li]:list-disc [&_ul]:space-y-1">
          {children}
        </div>
      </main>
      <LegalFooter />
    </div>
  );
}

export function LegalFooter() {
  return (
    <footer className="border-t border-black/5 py-8">
      <nav className="container-page flex flex-wrap gap-x-6 gap-y-2 text-sm text-ink-faint">
        <Link to="/privacy">Privacy</Link>
        <Link to="/terms">Terms</Link>
        <Link to="/support">Support</Link>
        <Link to="/impressum">Impressum</Link>
      </nav>
    </footer>
  );
}

function LangSwitch() {
  return (
    <p className="text-sm">
      <a className="underline" href="#en">English</a> ·{" "}
      <a className="underline" href="#de">Deutsch</a>
    </p>
  );
}

// ── Impressum ────────────────────────────────────────────────────────────────
export function ImpressumPage() {
  return (
    <LegalLayout title="Impressum">
      <p>Angaben gemäß § 5 DDG · Legal notice</p>
      <h2>Diensteanbieter</h2>
      <p>
        Felix Marlon Wieder
        <br />
        Kranichweg 58a
        <br />
        69123 Heidelberg
        <br />
        Deutschland
      </p>
      <h2>Kontakt</h2>
      <p>
        E-Mail: <Mail />
        <br />
        Telefon: +49 176 23497290
      </p>
      <h2>Verantwortlich für den Inhalt</h2>
      <p>Felix Marlon Wieder, Anschrift wie oben (§ 18 Abs. 2 MStV).</p>
      <h2>EU-Streitschlichtung</h2>
      <p>
        Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung bereit:{" "}
        <a className="underline" href="https://ec.europa.eu/consumers/odr">
          ec.europa.eu/consumers/odr
        </a>
        . Wir sind nicht verpflichtet und nicht bereit, an Streitbeilegungsverfahren vor einer
        Verbraucherschlichtungsstelle teilzunehmen.
      </p>
      <h2>Haftung für Links</h2>
      <p>
        Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen
        Einfluss haben. Für diese fremden Inhalte ist stets der jeweilige Anbieter verantwortlich.
        Bei Bekanntwerden von Rechtsverletzungen entfernen wir derartige Links umgehend.
      </p>
      <h2>Urheberrecht</h2>
      <p>
        Die durch den Anbieter erstellten Inhalte unterliegen dem deutschen Urheberrecht. Inhalte,
        die Nutzerinnen und Nutzer in die App einstellen, verbleiben bei diesen; siehe die{" "}
        <Link className="underline" to="/terms">Nutzungsbedingungen</Link>.
      </p>
    </LegalLayout>
  );
}

// ── Support ──────────────────────────────────────────────────────────────────
export function SupportPage() {
  return (
    <LegalLayout title="Support">
      <p>
        Questions, bugs, a report about content or another user, or a data request — write to{" "}
        <Mail />. One person runs EduPause and reads every message; you will usually hear back
        within two working days, and reports of abusive content are looked at within 24 hours.
      </p>
      <h2>Common questions</h2>
      <h3>How do I earn screen time?</h3>
      <p>
        Answer questions in the Train tab. Each correct answer adds unlock minutes to your wallet,
        which you can spend to open the apps you chose to block. Blocking is optional — the app
        works as a study tool without it.
      </p>
      <h3>How do I turn app blocking off?</h3>
      <p>
        Settings → App blocking. If you set a restriction password, you need it to weaken or turn
        off blocking. You can also revoke EduPause's Screen Time access in the iOS Settings app
        under Screen Time.
      </p>
      <h3>I forgot my restriction password</h3>
      <p>
        The restriction password is stored only on your device. Remove EduPause's Screen Time
        access in iOS Settings → Screen Time (or delete and reinstall the app), then set blocking
        up again.
      </p>
      <h3>How do I report a message, a user, a class or a set?</h3>
      <p>
        Press and hold a chat message and choose “Report message”, or use the ··· menu on a
        profile, class or public set. From the same menus you can block a user: their messages
        disappear for you immediately and they can no longer contact you.
      </p>
      <h3>How do I delete my account?</h3>
      <p>
        In the app: Settings (gear icon) → Delete Account. Deletion is immediate. What is removed
        is described in the <Link className="underline" to="/privacy">Privacy Policy</Link>.
        Without the app, email <Mail /> from the address on your account.
      </p>
      <h3>Generation says I've used my free generations</h3>
      <p>
        AI generation is free within a daily and monthly allowance. It refills automatically;
        the app shows when.
      </p>
      <p className="pt-4 text-sm">
        Operator: Felix Marlon Wieder, Kranichweg 58a, 69123 Heidelberg, Germany —{" "}
        <Link className="underline" to="/impressum">Impressum</Link>
      </p>
    </LegalLayout>
  );
}

// ── Terms ────────────────────────────────────────────────────────────────────
export function TermsPage() {
  return (
    <LegalLayout title="Terms of Use">
      <p>EduPause · Last updated {UPDATED_EN}</p>
      <LangSwitch />

      <section id="en">
        <h2>1. What this is</h2>
        <p>
          EduPause is a study app. It turns your material — a topic, notes, a PDF, photos of a
          lecture — into question sets with an AI model, lets you practise them, and can lock the
          apps you choose behind Screen Time until you have earned time by answering. It also has
          classes, chat and shared practice with people you invite. It is provided by the person
          named in the <Link className="underline" to="/impressum">Impressum</Link>. By creating
          an account you agree to these terms.
        </p>

        <h2>2. Your account</h2>
        <p>
          You must be at least 13 years old. Keep your login to yourself; you are responsible for
          what happens under your account. Do not impersonate anybody. You can delete your account
          at any time in the app under Settings → Delete Account; the{" "}
          <Link className="underline" to="/privacy">Privacy Policy</Link> says exactly what that
          deletes.
        </p>

        <h2>3. Zero tolerance for objectionable content and abusive users</h2>
        <p>
          EduPause has no tolerance for objectionable content or abusive behaviour. Do not post,
          upload, share or send — in chat, classes, profiles, names, shared sets or anywhere else:
        </p>
        <ul>
          <li>harassment, bullying, threats, hate speech, or content attacking people for who they are;</li>
          <li>sexual content of any kind involving minors, and sexual content sent to anyone who has not asked for it;</li>
          <li>violent, graphic or self-harm content;</li>
          <li>content that is illegal or infringes someone else's rights, including copyrighted material you are not allowed to share;</li>
          <li>other people's private information, or images of people who have not agreed;</li>
          <li>spam, scams, or attempts to break, overload or probe the service.</li>
        </ul>
        <p>
          We filter clearly abusive language in names and messages when they are posted. That is a
          floor, not a guarantee; reporting is the main mechanism.
        </p>

        <h2>4. Reporting, blocking, and what we do</h2>
        <p>
          Press and hold any message to report it or block its sender; profiles, classes and
          public sets have a Report option in their menu; or write to <Mail />. We review every
          report within 24 hours. We remove content that breaks section 3 and close the accounts
          of people who post it. Blocking a user hides their messages from you immediately and
          stops them contacting you; they are not told.
        </p>

        <h2>5. Your content stays yours</h2>
        <p>
          You keep every right in what you write and upload. You give us only the permission we
          need to run the service: to store and process your content (including sending it to the
          AI providers named in the Privacy Policy, after you have agreed to that in the app) and
          to show it to the people you share it with. That permission ends when you delete the
          content or your account. Only upload material you are allowed to use.
        </p>

        <h2>6. AI-generated content</h2>
        <p>
          Questions, answers, summaries, explanations and images are generated by AI models and
          can be wrong. Check anything you rely on — especially for exams, medicine, law or
          safety. Before the first time you use AI features, the app tells you what is sent and to
          whom, and asks.
        </p>

        <h2>7. Screen Time and unlock minutes</h2>
        <p>
          App blocking uses Apple's Screen Time framework and is optional. Unlock minutes have no
          monetary value, cannot be bought or sold, and may be adjusted if they were earned through
          a fault or abuse.
        </p>

        <h2>8. Free service and fair use</h2>
        <p>
          EduPause is free and has no in-app purchases. AI generation is limited to a daily and
          monthly allowance so the service stays affordable to run. The service may be unavailable
          at times, and features may change or be removed. Keep your own copy of anything you
          cannot afford to lose.
        </p>

        <h2>9. Liability</h2>
        <p>
          We are liable without limitation for intent and gross negligence, and for injury to
          life, body or health. For slight negligence we are liable only for breach of an essential
          contractual obligation, limited to the foreseeable damage typical for this kind of
          contract. Otherwise liability is excluded. Statutory consumer rights are unaffected.
        </p>

        <h2>10. Ending it</h2>
        <p>
          You can stop using EduPause and delete your account whenever you like. We may suspend or
          close an account that breaks section 3, and will say why unless the law prevents us.
        </p>

        <h2>11. Apple</h2>
        <p>
          These terms are between you and us, not Apple. Apple is not responsible for EduPause or
          its content, has no obligation to provide support for it, and is a third-party
          beneficiary of these terms as regards the iOS app. Apple's standard licensed application
          end user licence agreement applies in addition.
        </p>

        <h2>12. Law</h2>
        <p>
          German law applies. If you are a consumer, this does not take away the protection of the
          mandatory law of the country you live in.
        </p>
      </section>

      <section id="de" className="pt-8">
        <h2 className="!text-2xl">Nutzungsbedingungen</h2>
        <p>Stand: {UPDATED_DE}</p>

        <h2>1. Gegenstand</h2>
        <p>
          EduPause ist eine Lern-App. Sie erstellt mit einem KI-Modell aus deinem Material —
          einem Thema, Notizen, einem PDF, Fotos einer Vorlesung — Fragensets, lässt dich damit
          üben und kann ausgewählte Apps per Bildschirmzeit sperren, bis du dir durch richtige
          Antworten Zeit verdient hast. Dazu kommen Klassen, Chat und gemeinsames Üben mit Personen,
          die du einlädst. Anbieter ist die im{" "}
          <Link className="underline" to="/impressum">Impressum</Link> genannte Person. Mit der
          Erstellung eines Kontos akzeptierst du diese Bedingungen.
        </p>

        <h2>2. Dein Konto</h2>
        <p>
          Du musst mindestens 13 Jahre alt sein. Halte deine Zugangsdaten geheim; du bist für das
          verantwortlich, was unter deinem Konto geschieht. Gib dich nicht als jemand anderes aus.
          Du kannst dein Konto jederzeit in der App unter Einstellungen → Konto löschen entfernen;
          was dabei gelöscht wird, steht in der{" "}
          <Link className="underline" to="/privacy">Datenschutzerklärung</Link>.
        </p>

        <h2>3. Null Toleranz für anstößige Inhalte und missbräuchliches Verhalten</h2>
        <p>Nicht erlaubt sind — im Chat, in Klassen, Profilen, Namen, geteilten Sets oder anderswo:</p>
        <ul>
          <li>Belästigung, Mobbing, Drohungen, Hassrede oder Angriffe auf Menschen wegen ihrer Identität;</li>
          <li>sexuelle Inhalte jeglicher Art mit Minderjährigen sowie ungefragt zugesandte sexuelle Inhalte;</li>
          <li>gewaltverherrlichende, drastische oder selbstverletzende Inhalte;</li>
          <li>rechtswidrige Inhalte oder solche, die Rechte Dritter verletzen, einschließlich urheberrechtlich geschützten Materials, das du nicht teilen darfst;</li>
          <li>private Informationen anderer oder Bilder von Personen ohne deren Einverständnis;</li>
          <li>Spam, Betrug oder Versuche, den Dienst zu stören, zu überlasten oder auszuforschen.</li>
        </ul>
        <p>
          Eindeutig beleidigende Sprache in Namen und Nachrichten filtern wir beim Absenden. Das ist
          eine Untergrenze, keine Garantie; der wichtigste Mechanismus ist das Melden.
        </p>

        <h2>4. Melden, Blockieren und was wir tun</h2>
        <p>
          Halte eine Nachricht gedrückt, um sie zu melden oder den Absender zu blockieren; Profile,
          Klassen und öffentliche Sets haben eine Melden-Option im Menü; oder schreib an <Mail />.
          Wir prüfen jede Meldung innerhalb von 24 Stunden, entfernen Inhalte, die gegen Abschnitt
          3 verstoßen, und schließen die Konten der Verantwortlichen. Blockierte Personen können dich
          nicht mehr kontaktieren, ihre Nachrichten verschwinden für dich sofort; sie werden nicht
          benachrichtigt.
        </p>

        <h2>5. Deine Inhalte bleiben deine</h2>
        <p>
          Du behältst alle Rechte an dem, was du schreibst und hochlädst. Du räumst uns nur die
          Rechte ein, die wir für den Betrieb brauchen: deine Inhalte zu speichern und zu verarbeiten
          (einschließlich der Übermittlung an die in der Datenschutzerklärung genannten KI-Anbieter,
          nachdem du in der App zugestimmt hast) und sie den Personen zu zeigen, mit denen du sie
          teilst. Diese Rechte enden, wenn du die Inhalte oder dein Konto löschst. Lade nur Material
          hoch, das du verwenden darfst.
        </p>

        <h2>6. KI-generierte Inhalte</h2>
        <p>
          Fragen, Antworten, Zusammenfassungen, Erklärungen und Bilder werden von KI-Modellen
          erzeugt und können falsch sein. Prüfe alles, worauf du dich verlässt — besonders bei
          Prüfungen, Medizin, Recht oder Sicherheit. Vor der ersten Nutzung von KI-Funktionen sagt
          dir die App, was an wen gesendet wird, und fragt dich.
        </p>

        <h2>7. Bildschirmzeit und Freischalt-Minuten</h2>
        <p>
          Die App-Sperre nutzt Apples Bildschirmzeit-Framework und ist freiwillig. Freischalt-Minuten
          haben keinen Geldwert, können nicht gekauft oder verkauft werden und können angepasst
          werden, wenn sie durch einen Fehler oder Missbrauch entstanden sind.
        </p>

        <h2>8. Kostenloser Dienst und faire Nutzung</h2>
        <p>
          EduPause ist kostenlos und hat keine In-App-Käufe. Die KI-Erstellung ist auf ein tägliches
          und monatliches Kontingent begrenzt. Der Dienst kann zeitweise nicht verfügbar sein;
          Funktionen können sich ändern oder entfallen. Bewahre eine eigene Kopie von allem auf, das
          du nicht verlieren darfst.
        </p>

        <h2>9. Haftung</h2>
        <p>
          Wir haften unbeschränkt für Vorsatz und grobe Fahrlässigkeit sowie für Schäden aus der
          Verletzung des Lebens, des Körpers oder der Gesundheit. Bei leichter Fahrlässigkeit haften
          wir nur für die Verletzung einer wesentlichen Vertragspflicht, begrenzt auf den
          vorhersehbaren, vertragstypischen Schaden. Im Übrigen ist die Haftung ausgeschlossen.
          Gesetzliche Verbraucherrechte bleiben unberührt.
        </p>

        <h2>10. Beendigung</h2>
        <p>
          Du kannst EduPause jederzeit nicht mehr nutzen und dein Konto löschen. Wir können Konten,
          die gegen Abschnitt 3 verstoßen, sperren oder schließen und nennen den Grund, soweit das
          Gesetz es nicht verbietet.
        </p>

        <h2>11. Apple</h2>
        <p>
          Diese Bedingungen gelten zwischen dir und uns, nicht Apple. Apple ist für EduPause und
          seine Inhalte nicht verantwortlich, nicht zum Support verpflichtet und hinsichtlich der
          iOS-App Drittbegünstigter dieser Bedingungen. Zusätzlich gilt Apples Standard-EULA für
          lizenzierte Apps.
        </p>

        <h2>12. Recht</h2>
        <p>
          Es gilt deutsches Recht. Als Verbraucherin oder Verbraucher behältst du den Schutz der
          zwingenden Vorschriften deines Wohnsitzlandes.
        </p>
      </section>
    </LegalLayout>
  );
}

// ── Privacy ──────────────────────────────────────────────────────────────────
export function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy">
      <p>EduPause · Last updated {UPDATED_EN}</p>
      <LangSwitch />

      <section id="en">
        <h2>Who is responsible</h2>
        <p>
          Felix Marlon Wieder, Kranichweg 58a, 69123 Heidelberg, Germany — <Mail />. This policy
          covers the EduPause iOS app and the website edupause.app.
        </p>

        <h2>What we collect and why</h2>
        <ul>
          <li>
            <strong>Account:</strong> email address, username, optional display name and profile
            picture, date of birth (only to check you are at least 13), country, and, if you sign in
            with Apple, the identifier Apple gives us (which may be a private relay address). Purpose:
            running your account (Art. 6(1)(b) GDPR).
          </li>
          <li>
            <strong>Your study content:</strong> topics and text you enter, files, PDFs and photos you
            upload, the question sets, summaries and projects generated from them, and your markings.
            Purpose: providing the service (Art. 6(1)(b)).
          </li>
          <li>
            <strong>Learning activity:</strong> your answers, practice progress, scores, earned and
            spent unlock minutes. Purpose: progress tracking, adaptive practice and the minute wallet
            (Art. 6(1)(b)).
          </li>
          <li>
            <strong>Community:</strong> classes you create or join, messages, shared sets, friend
            connections, reports and blocks. Purpose: the community features and keeping them safe
            (Art. 6(1)(b) and (f)).
          </li>
          <li>
            <strong>Technical data:</strong> IP address and request logs on our server, kept for up to
            14 days for security and troubleshooting (Art. 6(1)(f)).
          </li>
        </ul>
        <p>
          <strong>Screen Time:</strong> the apps you choose to block are selected through Apple's
          Screen Time framework. Apple gives the app opaque tokens, not the names of your apps, and
          they stay on your device. Your restriction password is stored only in your device's
          Keychain.
        </p>
        <p>
          We use no advertising, no analytics or crash-reporting SDKs, and no tracking. We do not sell
          data.
        </p>

        <h2>AI processing</h2>
        <p>
          When you generate questions, summaries, explanations or images, the relevant text, files or
          images are sent to an AI provider to produce the result. The app asks for your permission
          before the first time and you can withdraw it in Settings → AI data sharing. Providers used:
        </p>
        <ul>
          <li>DeepSeek (Hangzhou DeepSeek Artificial Intelligence Co., China) — default text model;</li>
          <li>Google Gemini (Google LLC / Google Ireland Ltd.) — text, reading images and PDFs;</li>
          <li>OpenAI (OpenAI, L.L.C., USA / OpenAI Ireland Ltd.) — images and some text tasks.</li>
        </ul>
        <p>
          If you choose a different engine in Settings → AI engine, your content goes to that
          provider instead. The providers process the data to answer the request under their API
          terms; under those terms API data is not used for advertising. Transfers outside the EU
          rely on the EU Standard Contractual Clauses or, for US providers, the EU–US Data Privacy
          Framework where the provider is certified. China has no EU adequacy decision; if you do
          not want your content processed there, pick a different engine in Settings → AI engine.
          Please do not upload sensitive personal data (health data, other people's private
          information) as study material.
        </p>

        <h2>Other processors</h2>
        <ul>
          <li>Hetzner Online GmbH (Germany) — our server and database, located in Helsinki, Finland (EU).</li>
          <li>Cloudflare, Inc. (R2 storage) — stores uploaded files, generated images and profile pictures.</li>
          <li>Resend, Inc. — sends account emails (verification, password reset) and report notifications to us.</li>
          <li>Apple — Sign in with Apple and, if enabled, push notifications.</li>
          <li>Crossref — receives search terms (not personal data) when the app looks up citations.</li>
        </ul>

        <h2>Who else sees your data</h2>
        <p>
          Your username, display name and profile picture are visible to people in your classes, your
          friends and people who search for you. Messages are visible to the members of the chat.
          Sets you share are visible to the people or classes you share them with. Nothing else is
          public.
        </p>

        <h2>How long we keep it, and deleting your account</h2>
        <p>
          We keep your data while your account exists. Settings → Delete Account deletes your account
          immediately: your profile, name, username, date of birth, profile picture, sign-in links
          (including your Sign in with Apple connection, which we also revoke with Apple), friends,
          blocks, and your private content — uploaded files, question sets, projects, study
          documents and progress. Content you shared into a class (sets, homework) stays available to
          that class without your name, and messages you sent in group chats remain visible to their
          members without your name. Reports you filed are kept without your identity while needed
          to handle them. Database backups are overwritten after 30 days, so deleted data also
          disappears from backups within that time.
        </p>

        <h2>Your rights</h2>
        <p>
          You can ask for access, correction, deletion, restriction, data portability, and object to
          processing based on legitimate interests (Art. 15–21 GDPR), and withdraw consent at any time.
          Write to <Mail />. You can complain to a data protection authority, for example the
          Landesbeauftragte für den Datenschutz und die Informationsfreiheit Baden-Württemberg.
        </p>

        <h2>Children</h2>
        <p>EduPause is for people aged 13 and over. We delete accounts we learn belong to younger children.</p>

        <h2>Changes</h2>
        <p>We will update this page when our processing changes and say so in the app for significant changes.</p>
      </section>

      <section id="de" className="pt-8">
        <h2 className="!text-2xl">Datenschutzerklärung</h2>
        <p>Stand: {UPDATED_DE}</p>

        <h2>Verantwortlicher</h2>
        <p>
          Felix Marlon Wieder, Kranichweg 58a, 69123 Heidelberg, Deutschland — <Mail />. Diese
          Erklärung gilt für die EduPause-iOS-App und die Website edupause.app.
        </p>

        <h2>Welche Daten wir verarbeiten und warum</h2>
        <ul>
          <li>
            <strong>Konto:</strong> E-Mail-Adresse, Benutzername, optional Anzeigename und Profilbild,
            Geburtsdatum (nur zur Prüfung des Mindestalters von 13 Jahren), Land sowie bei „Mit Apple
            anmelden“ die von Apple übermittelte Kennung (ggf. eine private Weiterleitungsadresse).
            Zweck: Kontoführung (Art. 6 Abs. 1 lit. b DSGVO).
          </li>
          <li>
            <strong>Lerninhalte:</strong> eingegebene Themen und Texte, hochgeladene Dateien, PDFs und
            Fotos, daraus erzeugte Fragensets, Zusammenfassungen und Projekte sowie deine Markierungen.
            Zweck: Erbringung des Dienstes (Art. 6 Abs. 1 lit. b).
          </li>
          <li>
            <strong>Lernaktivität:</strong> Antworten, Lernfortschritt, Ergebnisse, verdiente und
            genutzte Freischalt-Minuten (Art. 6 Abs. 1 lit. b).
          </li>
          <li>
            <strong>Community:</strong> Klassen, Nachrichten, geteilte Sets, Freundschaften, Meldungen
            und Blockierungen. Zweck: Community-Funktionen und deren Sicherheit (Art. 6 Abs. 1 lit. b
            und f).
          </li>
          <li>
            <strong>Technische Daten:</strong> IP-Adresse und Server-Protokolle, bis zu 14 Tage zur
            Sicherheit und Fehlersuche (Art. 6 Abs. 1 lit. f).
          </li>
        </ul>
        <p>
          <strong>Bildschirmzeit:</strong> Die zu sperrenden Apps wählst du über Apples
          Bildschirmzeit-Framework aus. Die App erhält nur anonyme Tokens, nicht die Namen deiner
          Apps; sie bleiben auf deinem Gerät. Dein Sperr-Passwort liegt nur im Schlüsselbund deines
          Geräts.
        </p>
        <p>Keine Werbung, keine Analyse- oder Crash-SDKs, kein Tracking, kein Verkauf von Daten.</p>

        <h2>KI-Verarbeitung</h2>
        <p>
          Wenn du Fragen, Zusammenfassungen, Erklärungen oder Bilder erzeugst, werden die nötigen
          Texte, Dateien oder Bilder an einen KI-Anbieter gesendet. Die App fragt dich vor der ersten
          Nutzung um Erlaubnis; du kannst sie unter Einstellungen → KI-Datenweitergabe widerrufen.
          Anbieter: DeepSeek (China) als Standard-Textmodell, Google Gemini (Google LLC / Google
          Ireland Ltd.) für Text sowie das Lesen von Bildern und PDFs, OpenAI (USA / Irland) für
          Bilder und einzelne Textaufgaben. Wählst du unter Einstellungen → KI-Engine einen anderen
          Anbieter, gehen deine Inhalte an diesen. Übermittlungen außerhalb der EU erfolgen auf
          Grundlage der EU-Standardvertragsklauseln bzw. des EU-US Data Privacy Framework, soweit der
          Anbieter zertifiziert ist. Für China besteht kein Angemessenheitsbeschluss; wenn du das
          nicht möchtest, wähle unter Einstellungen → KI-Engine einen anderen Anbieter. Bitte lade
          keine sensiblen personenbezogenen Daten als Lernmaterial hoch.
        </p>

        <h2>Weitere Auftragsverarbeiter</h2>
        <ul>
          <li>Hetzner Online GmbH — Server und Datenbank, Standort Helsinki, Finnland (EU).</li>
          <li>Cloudflare, Inc. (R2) — Speicherung von Dateien, erzeugten Bildern und Profilbildern.</li>
          <li>Resend, Inc. — Versand von Konto-E-Mails und Meldungsbenachrichtigungen an uns.</li>
          <li>Apple — „Mit Apple anmelden“ und ggf. Push-Mitteilungen.</li>
          <li>Crossref — erhält Suchbegriffe (keine personenbezogenen Daten) für Quellenangaben.</li>
        </ul>

        <h2>Wer deine Daten sieht</h2>
        <p>
          Benutzername, Anzeigename und Profilbild sehen Mitglieder deiner Klassen, deine Freunde und
          Personen, die dich suchen. Nachrichten sehen die Mitglieder des Chats, geteilte Sets die
          Personen oder Klassen, mit denen du sie teilst. Sonst ist nichts öffentlich.
        </p>

        <h2>Speicherdauer und Kontolöschung</h2>
        <p>
          Wir speichern deine Daten, solange dein Konto besteht. Einstellungen → Konto löschen löscht
          dein Konto sofort: Profil, Name, Benutzername, Geburtsdatum, Profilbild,
          Anmeldeverknüpfungen (einschließlich „Mit Apple anmelden“, die wir auch bei Apple
          widerrufen), Freundschaften, Blockierungen und deine privaten Inhalte — hochgeladene
          Dateien, Fragensets, Projekte, Lerndokumente und Fortschritt. In eine Klasse geteilte
          Inhalte (Sets, Hausaufgaben) bleiben der Klasse ohne deinen Namen erhalten, ebenso
          Nachrichten in Gruppenchats. Meldungen, die du abgegeben hast, bleiben ohne Bezug zu dir
          erhalten, solange sie bearbeitet werden. Datenbank-Backups werden nach 30 Tagen
          überschrieben.
        </p>

        <h2>Deine Rechte</h2>
        <p>
          Du hast das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung,
          Datenübertragbarkeit und Widerspruch (Art. 15–21 DSGVO) sowie auf jederzeitigen Widerruf
          einer Einwilligung. Schreib an <Mail />. Du kannst dich bei einer Aufsichtsbehörde
          beschweren, z. B. beim Landesbeauftragten für den Datenschutz und die Informationsfreiheit
          Baden-Württemberg.
        </p>

        <h2>Kinder</h2>
        <p>EduPause ist ab 13 Jahren. Konten jüngerer Kinder löschen wir, sobald wir davon erfahren.</p>
      </section>
    </LegalLayout>
  );
}
